/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: usePermissionsUI.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing permissions UI state (expansion, search, filtering).
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';

interface MenuItem {
  id: number;
  level: number;
  parentId: number | null;
  title?: string;
  description?: string;
  url?: string;
  /** Lucide icon name or component reference */
  icon?: string;
  /** Unique module slug used for permission keys and i18n labels */
  slug?: string;
  children?: MenuItem[];
}

export function usePermissionsUI(menuItems: MenuItem[], mode: 'create' | 'edit') {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Ensure initial state is collapsed when menuItems are loaded
  useEffect(() => {
    if (menuItems.length > 0 && mode === 'create') {
      setExpandedSections(new Set());
      setExpandedGroups(new Set());
    }
  }, [menuItems, mode]);

  const toggleSection = (sectionId: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
        // Also collapse all groups in this section
        const groups = menuItems.filter(item => item.parentId === sectionId);
        groups.forEach(group => {
          setExpandedGroups(prevGroups => {
            const newGroupSet = new Set(prevGroups);
            newGroupSet.delete(group.id);
            return newGroupSet;
          });
        });
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const toggleGroup = (groupId: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    const allSections = menuItems.filter(item => item.level === 1).map(item => item.id);
    const allGroups = menuItems.filter(item => item.level === 2).map(item => item.id);
    const allExpanded = allSections.every(sectionId => expandedSections.has(sectionId)) &&
      allGroups.every(groupId => expandedGroups.has(groupId));

    if (allExpanded) {
      setExpandedSections(new Set());
      setExpandedGroups(new Set());
    } else {
      setExpandedSections(new Set(allSections));
      setExpandedGroups(new Set(allGroups));
    }
  };

  const areAllExpanded = () => {
    const allSections = menuItems.filter(item => item.level === 1).map(item => item.id);
    const allGroups = menuItems.filter(item => item.level === 2).map(item => item.id);
    return allSections.length > 0 && allGroups.length > 0 &&
      allSections.every(sectionId => expandedSections.has(sectionId)) &&
      allGroups.every(groupId => expandedGroups.has(groupId));
  };

  const organizeMenuItems = useMemo(() => {
    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      if (!searchQuery.trim()) {
        return items;
      }

      const query = searchQuery.toLowerCase();
      return items.filter(item => {
        const title = item.title?.toLowerCase() || '';
        const description = item.description?.toLowerCase() || '';
        const url = item.url?.toLowerCase() || '';

        return title.includes(query) ||
          description.includes(query) ||
          url.includes(query);
      });
    };

    const getFilteredSections = (): MenuItem[] => {
      if (!searchQuery.trim()) {
        return menuItems.filter(item => item.level === 1);
      }

      const sections = menuItems.filter(item => item.level === 1);
      return sections.filter(section => {
        const sectionMatches = filterMenuItems([section]).length > 0;
        if (sectionMatches) return true;

        const groups = menuItems.filter(item => item.level === 2 && item.parentId === section.id);
        const matchingGroups = filterMenuItems(groups);
        if (matchingGroups.length > 0) return true;

        for (const group of groups) {
          const items = menuItems.filter(item => item.level === 3 && item.parentId === group.id);
          const matchingItems = filterMenuItems(items);
          if (matchingItems.length > 0) return true;
        }

        return false;
      });
    };

    const getFilteredGroups = (sectionId: number): MenuItem[] => {
      const groups = menuItems.filter(item => item.level === 2 && item.parentId === sectionId);

      if (!searchQuery.trim()) {
        return groups;
      }

      return groups.filter(group => {
        const groupMatches = filterMenuItems([group]).length > 0;
        if (groupMatches) return true;

        const items = menuItems.filter(item => item.level === 3 && item.parentId === group.id);
        const matchingItems = filterMenuItems(items);
        return matchingItems.length > 0;
      });
    };

    const getFilteredItems = (groupId: number): MenuItem[] => {
      const items = menuItems.filter(item => item.level === 3 && item.parentId === groupId);
      return filterMenuItems(items);
    };

    // Check if a group has any level 3 children
    const hasLevel3Children = (groupId: number): boolean => {
      return menuItems.some(item => item.level === 3 && item.parentId === groupId);
    };

    const sections = getFilteredSections();
    return sections.map(section => {
      const groups = getFilteredGroups(section.id);

      return {
        ...section,
        children: groups.map(group => {
          // If group has level 3 children, include them normally
          // If group has NO level 3 children, treat group itself as the "item" 
          // by returning it with an empty children array but marking it as a final item
          const children = getFilteredItems(group.id);

          return {
            ...group,
            children: children,
            // Flag to indicate this is a "leaf" group (no level 3 children)
            isLeafGroup: !hasLevel3Children(group.id)
          };
        })
      };
    });
  }, [menuItems, searchQuery]);

  return {
    expandedSections,
    expandedGroups,
    searchQuery,
    setSearchQuery,
    toggleSection,
    toggleGroup,
    handleToggleAll,
    areAllExpanded,
    organizeMenuItems,
  };
}

